// Keyboard input, split by role:
// - Architect: discrete per-keypress piece move/rotate/place, plus gold-spend
//   abilities (1/2/3 arm, click to confirm, Esc/right-click to cancel) and
//   permanent upgrades (4/5/6/7, instant).
// - Hero: continuous movement — held keys form a direction vector that's
//   sent whenever it changes (not every frame), matching the "state, not
//   events" input model described in doc/game_design.md's plan — plus
//   right-click move-to-point, and spells (Q/E/R arm a preview, left-click
//   confirms, any spell key cancels) aimed at a continuously-streamed
//   world-space cursor position (see initPointerInput).

import type { Net } from "./net";
import { type Camera, screenToWorld } from "./render";
import { type ArchitectAbility, type ArchitectUpgrade, isHeroRole, type Role, type SpellSlot, type WorldPoint } from "../shared/types";

const ARCHITECT_MOVE_KEYS: Record<string, "up" | "down" | "left" | "right"> = {
  ArrowUp: "up",
  w: "up",
  W: "up",
  ArrowDown: "down",
  s: "down",
  S: "down",
  ArrowLeft: "left",
  a: "left",
  A: "left",
  ArrowRight: "right",
  d: "right",
  D: "right",
};

export function initArchitectInput(net: Net, getRole: () => Role | null): void {
  window.addEventListener("keydown", (event) => {
    if (getRole() !== "architect") return;

    const dir = ARCHITECT_MOVE_KEYS[event.key];
    if (dir) {
      net.send({ type: "architectMove", dir });
      event.preventDefault();
      return;
    }
    if (event.key === "r" || event.key === "R") {
      net.send({ type: "architectRotate" });
      event.preventDefault();
    } else if (event.key === " " || event.key === "Enter") {
      net.send({ type: "architectPlace" });
      event.preventDefault();
    }
  });
}

type HeroDir = "up" | "down" | "left" | "right";

function keyToHeroDir(key: string): HeroDir | null {
  switch (key) {
    case "ArrowUp":
    case "w":
    case "W":
      return "up";
    case "ArrowDown":
    case "s":
    case "S":
      return "down";
    case "ArrowLeft":
    case "a":
    case "A":
      return "left";
    case "ArrowRight":
    case "d":
    case "D":
      return "right";
    default:
      return null;
  }
}

export function initHeroInput(net: Net, getRole: () => Role | null): void {
  const held = new Set<HeroDir>();
  let lastSentX = 0;
  let lastSentY = 0;

  function sendIfChanged(): void {
    // World y increases toward the unbuilt front of the dungeon (see
    // render.ts's camera convention), so "up" is +y and "down" is -y.
    const x = (held.has("right") ? 1 : 0) - (held.has("left") ? 1 : 0);
    const y = (held.has("up") ? 1 : 0) - (held.has("down") ? 1 : 0);
    if (x === lastSentX && y === lastSentY) return;
    lastSentX = x;
    lastSentY = y;
    net.send({ type: "heroInput", move: { x, y } });
  }

  window.addEventListener("keydown", (event) => {
    const role = getRole();
    if (!role || role === "architect") return;
    const dir = keyToHeroDir(event.key);
    if (!dir) return;
    held.add(dir);
    sendIfChanged();
    event.preventDefault();
  });

  window.addEventListener("keyup", (event) => {
    const dir = keyToHeroDir(event.key);
    if (!dir) return;
    held.delete(dir);
    sendIfChanged();
  });

  // If the tab loses focus mid-move, the corresponding keyup can be missed
  // entirely — without this a hero would silently keep walking forever.
  window.addEventListener("blur", () => {
    held.clear();
    sendIfChanged();
  });
}

const SPELL_KEY_SLOTS: Record<string, SpellSlot> = { q: 1, Q: 1, e: 2, E: 2, r: 3, R: 3 };
const ARCHITECT_ABILITY_KEYS: Record<string, ArchitectAbility> = { "1": "meteor", "2": "spawnMinions", "3": "spawnSuperMinion" };
const ARCHITECT_UPGRADE_KEYS: Record<string, ArchitectUpgrade> = {
  "4": "minionSpeed",
  "5": "minionHealth",
  "6": "minionDamage",
  "7": "minionCount",
};

export interface PointerInput {
  getArmedAbility: () => ArchitectAbility | null;
  getArmedSpellSlot: () => SpellSlot | null;
  getMouseWorld: () => WorldPoint | null;
  /** Raw screen-space cursor position (canvas-local px), for HUD elements that follow the cursor directly rather than a world point — see src/render.ts's drawSpellCastHint. */
  getMouseScreen: () => { x: number; y: number } | null;
}

/**
 * Owns the mouse-cursor -> world-space conversion (camera-aware, since the
 * camera scrolls every frame — see doc/game_design.md's plan notes on why
 * this was cut from v1 originally) and everything that hangs off left/right
 * click for both roles:
 * - Architect: 1/2/3 arms a gold-spend ability (preview follows the cursor
 *   — see src/render.ts's AbilityPreview), left-click confirms, Escape or
 *   right-click cancels.
 * - Hero: Q/E/R arms a spell (range/AOE preview follows the cursor — see
 *   src/render.ts's HeroSpellPreview), left-click confirms, *any* spell key
 *   (the armed one or a different one) or Escape cancels — armed spells
 *   never auto-cast just because a different key was pressed, you always
 *   get a fresh look at the preview before committing. Right-click issues a
 *   move-to-point command independent of any armed spell — the two are
 *   orthogonal, so you can reposition while still deciding where to aim.
 *   Heroes also stream their aim point continuously regardless of arm state
 *   (see shared/types.ts's "heroAim") since the server always resolves a
 *   cast against the caster's latest aim.
 */
export function initPointerInput(net: Net, canvas: HTMLCanvasElement, getRole: () => Role | null, getCamera: () => Camera | null): PointerInput {
  let screenPos: { x: number; y: number } | null = null;
  let armedAbility: ArchitectAbility | null = null;
  let armedSpellSlot: SpellSlot | null = null;
  let lastSentAim: WorldPoint | null = null;
  let rightButtonDown = false; // hero move-to fires continuously while held — see the interval below

  function currentWorld(): WorldPoint | null {
    const camera = getCamera();
    if (!screenPos || !camera) return null;
    return screenToWorld(camera, screenPos.x, screenPos.y);
  }

  canvas.addEventListener("mousemove", (event) => {
    const rect = canvas.getBoundingClientRect();
    screenPos = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  });
  canvas.addEventListener("mouseleave", () => {
    screenPos = null;
  });

  canvas.addEventListener("click", () => {
    const role = getRole();
    if (role === "architect") {
      if (!armedAbility) return;
      const world = currentWorld();
      if (!world) return;
      net.send({ type: "architectUseAbility", ability: armedAbility, point: world });
      armedAbility = null;
    } else if (role && isHeroRole(role)) {
      if (armedSpellSlot === null) return;
      net.send({ type: "heroCastSpell", slot: armedSpellSlot });
      armedSpellSlot = null;
    }
  });
  // Right mouse button: for the architect, an instant cancel of whatever
  // ability is armed. For a hero, move-to — fires immediately on press *and*
  // keeps firing every interval tick while held (see below), so dragging
  // the held button steers the hero continuously instead of only issuing a
  // single one-shot command at the point where the button first went down.
  canvas.addEventListener("mousedown", (event) => {
    if (event.button !== 2) return;
    rightButtonDown = true;
    const role = getRole();
    if (role === "architect") {
      armedAbility = null;
    } else if (role && isHeroRole(role)) {
      const world = currentWorld();
      if (world) net.send({ type: "heroMoveTo", point: world });
    }
  });
  // Listened on window, not canvas, so a release after dragging the cursor
  // off the canvas still clears the held state.
  window.addEventListener("mouseup", (event) => {
    if (event.button === 2) rightButtonDown = false;
  });
  canvas.addEventListener("contextmenu", (event) => event.preventDefault()); // suppress the OS menu only — the actual handling is on mousedown/mouseup above

  window.addEventListener("keydown", (event) => {
    const role = getRole();
    if (role === "architect") {
      const ability = ARCHITECT_ABILITY_KEYS[event.key];
      if (ability) {
        armedAbility = armedAbility === ability ? null : ability; // press again to disarm
        event.preventDefault();
        return;
      }
      const upgrade = ARCHITECT_UPGRADE_KEYS[event.key];
      if (upgrade) {
        net.send({ type: "architectBuyUpgrade", upgrade });
        event.preventDefault();
        return;
      }
      if (event.key === "Escape") armedAbility = null;
    } else if (role && isHeroRole(role)) {
      const slot = SPELL_KEY_SLOTS[event.key];
      if (slot) {
        // Any spell key while one is armed just cancels it — you always get
        // a fresh press to arm the next one, never an accidental switch.
        armedSpellSlot = armedSpellSlot === null ? slot : null;
        event.preventDefault();
        return;
      }
      if (event.key === "Escape") armedSpellSlot = null;
    }
  });

  // Throttled rather than sent every frame — a hero's aim (and, while the
  // right button is held, the move-to target) doesn't need tick-rate
  // precision, and this keeps the socket quiet when idle.
  window.setInterval(() => {
    const role = getRole();
    if (!role || role === "architect") return;
    const world = currentWorld();
    if (!world) return;
    if (rightButtonDown) net.send({ type: "heroMoveTo", point: world }); // keep steering toward the cursor while held, not just on the initial press
    if (lastSentAim && Math.hypot(world.x - lastSentAim.x, world.y - lastSentAim.y) < 0.05) return;
    lastSentAim = world;
    net.send({ type: "heroAim", point: world });
  }, 50);

  return {
    getArmedAbility: () => armedAbility,
    getArmedSpellSlot: () => armedSpellSlot,
    getMouseWorld: currentWorld,
    getMouseScreen: () => screenPos,
  };
}
